/**
 * A5 — the transcript page.
 *
 * The page frame, the header metadata and the whole download row are
 * server-rendered from the template context. This module adds three things
 * (and pages/transcript-actions.ts the language model's, SEO_BRIEF_09 Part C, and lib/share.ts
 * the Share button's, SEO_BRIEF_11 section 5):
 * the transcript body (which is fetched, because `GET /api/transcripts/{id}`
 * is the only route that returns transcript data), the paragraph/timestamp
 * toggle, and Copy. A copy, by the button or by selecting the text, is counted (lib/copies.ts).
 *
 * The download row is NOT built here. `formats[]` arrives already grouped and
 * already resolved against the transcript's capabilities — including
 * `available: false, unavailable_reason: "no_word_timing"`, which is what
 * disables Enhanced LRC, karaoke ASS and word-level VTT on a caption-sourced
 * transcript. The server computes it from `word_timestamps`; re-deriving it
 * here would be a second implementation of a rule that has one.
 */

import { ApiError, getTranscript } from '../lib/api.ts';
import type { TranscriptDocument } from '../lib/api-types.ts';
import { copyText } from '../lib/clipboard.ts';
import { countCopy, watchCopies } from '../lib/copies.ts';
import { data, el, must, on, qs, qsa, setData } from '../lib/dom.ts';
import { track } from '../lib/events.ts';
import { stamp } from '../lib/format.ts';
import { mountShare } from '../lib/share.ts';
import { showProblem } from '../lib/states.ts';
import { toast } from '../lib/toast.ts';
import { mountTranscriptActions } from './transcript-actions.ts';

/** A gap this long, after a sentence ending, starts a new paragraph. */
const PARAGRAPH_GAP_S = 1.6;
/** Or this many characters, so a monologue still breaks somewhere. */
const PARAGRAPH_MAX_CHARS = 700;
/**
 * This page's name in the usage counter (lib/usage.ts), the one the transcript's downloads are
 * counted under (pages/format.ts): a copy of the transcript sits beside them on the stats page.
 */
const USAGE_TOOL = 'transcript';

export interface Paragraph {
  readonly start: number;
  readonly text: string;
}

/**
 * Characters of the scripts that write words with no space between them (Han,
 * the two kana, Thai and its Lao, Khmer and Myanmar neighbours), plus the CJK
 * and full-width punctuation that sits among them. `sc=`, not `scx=`: the
 * script extensions put the Latin middle dot in Han.
 */
const NO_SPACE_CLASS =
  '[\\p{sc=Han}\\p{sc=Hiragana}\\p{sc=Katakana}\\p{sc=Thai}\\p{sc=Lao}\\p{sc=Khmer}' +
  '\\p{sc=Myanmar}\\u3000-\\u303f\\u30a0\\u30fb\\u30fc\\uff00-\\uffef]';
const NO_SPACE_LAST = new RegExp(`${NO_SPACE_CLASS}$`, 'u');
const NO_SPACE_FIRST = new RegExp(`^${NO_SPACE_CLASS}`, 'u');

/**
 * What goes between two segment texts when they are joined: a space, or
 * nothing.
 *
 * Whisper's segments carry their own leading space, so they need nothing. A
 * caption cue (the caption route passes `cue.text` through as it is) has
 * none, and concatenating two of them ran the words together: "you think" +
 * "are" read "you thinkare". A space is added only where neither side already
 * has one and neither boundary character belongs to a script that does not
 * use spaces, because a space between two Han characters is a typo.
 */
export function joinSeparator(left: string, right: string): '' | ' ' {
  if (left === '' || right === '') return '';
  if (/\s$/u.test(left) || /^\s/u.test(right)) return '';
  if (NO_SPACE_LAST.test(left) || NO_SPACE_FIRST.test(right)) return '';
  return ' ';
}

/**
 * Group segments into paragraphs.
 *
 * Whisper's segments are model artefacts, not paragraphs — they break on
 * decode windows. Reading a transcript as one line per segment is the
 * experience this grouping exists to avoid. Joining is never a blanket
 * join-with-a-space, which doubles Whisper's own leading space and destroys
 * every script that does not use spaces; `joinSeparator` adds one only where
 * the two texts would otherwise run together.
 */
export function paragraphs(segments: TranscriptDocument['segments']): Paragraph[] {
  const out: Paragraph[] = [];
  let current = '';
  let start = 0;
  let previousEnd = 0;

  for (const segment of segments) {
    if (current === '') {
      start = segment.start;
    } else {
      const gap = segment.start - previousEnd;
      const ended = /[.!?…。！？]["'”’)\]]?\s*$/.test(current);
      if ((gap >= PARAGRAPH_GAP_S && ended) || current.length >= PARAGRAPH_MAX_CHARS) {
        out.push({ start, text: current.trim() });
        current = '';
        start = segment.start;
      }
    }
    current += joinSeparator(current, segment.text) + segment.text;
    previousEnd = segment.end;
  }
  if (current.trim() !== '') out.push({ start, text: current.trim() });
  return out;
}

/** The text Copy puts on the clipboard, for whichever mode is visible. */
export function transcriptText(doc: TranscriptDocument, mode: 'paragraphs' | 'timestamps'): string {
  if (mode === 'timestamps') {
    return doc.segments.map((s) => `[${stamp(s.start)}] ${s.text.trim()}`).join('\n');
  }
  return paragraphs(doc.segments)
    .map((p) => p.text)
    .join('\n\n');
}

export function mountTranscriptPage(): void {
  const root = qs<HTMLElement>('[data-transcript]');
  if (!root) return;
  const publicId = data(root, 'public-id');
  if (!publicId) return;

  const body = must<HTMLElement>('[data-transcript-body]', root);
  const region = must<HTMLElement>('#banner-region');
  const copyButtons = qsa<HTMLButtonElement>('[data-copy-transcript]');
  const toggles = qsa<HTMLElement>('[data-mode-toggle]');
  let doc: TranscriptDocument | null = null;
  let mode: 'paragraphs' | 'timestamps' = 'paragraphs';

  function paint(): void {
    if (!doc) return;
    setData(body, 'mode', mode);
    body.replaceChildren();
    if (mode === 'timestamps') {
      for (const segment of doc.segments) {
        const line = el('p', {}, []);
        // The gutter links to the source at that offset where the platform
        // supports it; without a URL it stays plain text rather than a dead
        // link.
        line.append(el('span', { class: 'transcript__t' }, [`[${stamp(segment.start)}]`]));
        line.append(el('span', {}, [segment.text.trim()]));
        body.append(line);
      }
      return;
    }
    for (const para of paragraphs(doc.segments)) {
      body.append(el('p', {}, [para.text]));
    }
  }

  for (const toggle of toggles) {
    on(toggle, 'click', () => {
      const want = data(toggle, 'mode-toggle');
      if (want !== 'paragraphs' && want !== 'timestamps') return;
      mode = want;
      for (const other of toggles) {
        other.setAttribute('aria-pressed', other === toggle ? 'true' : 'false');
      }
      paint();
    });
  }

  for (const button of copyButtons) {
    on(button, 'click', () => {
      // No await before the clipboard call: the document is already in memory
      // and Safari drops transient activation across one.
      if (!doc) return;
      void copyText(transcriptText(doc, mode)).then((ok) => {
        if (!ok) return;
        toast(data(button, 'copied-message') ?? 'Copied');
        countCopy(USAGE_TOOL);
      });
    });
  }
  // The transcript's own text selected and copied: once per page view (lib/copies.ts).
  watchCopies([body], USAGE_TOOL);

  const skeleton = qs<HTMLElement>('[data-transcript-skeleton]', root);

  void getTranscript(publicId)
    .then((document_) => {
      doc = document_;
      if (skeleton) skeleton.hidden = true;
      paint();
    })
    .catch((err: unknown) => {
      if (skeleton) skeleton.hidden = true;
      if (err instanceof ApiError) showProblem(region, err.problem);
      else throw err;
    });

  // Each format chip is its own page and its own page view — that is the
  // acquisition model. The counter fires here, on the way out.
  for (const chip of qsa<HTMLAnchorElement>('[data-format-chip]')) {
    on(chip, 'click', () => {
      const format = data(chip, 'format-chip');
      if (format) track('format_download', { format });
    });
  }

  // The Share button beside the downloads (SEO_BRIEF_11 section 5).
  mountShare(root);

  // Translate, "Translate to English", Summarize and `?do=` (SEO_BRIEF_09 Part C), where the
  // server rendered them.
  mountTranscriptActions(root, publicId);
}
